import { Box } from '@mui/material';
import { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
    BodyContourIcon,
    BoolIcon,
    CouchIcon,
    MarginIcon,
} from '~/assets/workspace-icons';
import { useStructureToolGating } from '~/hooks/target/useStructureToolGating';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { useAcquiredPrimarySliceShown } from '~/hooks/environment/useEnvironmentViews';
import {
    CouchPlacementBlocker,
    couchPlacementBlocker,
} from '~/domain/couchtop/placementBlocker';
import type { Image3D } from '~/domain/patient/image3d';
import { operationPanelState } from '~/state/atoms/toolsAtoms';
import { useRecoilState } from 'recoil';
import { useSetOperationPanelUnlessBusy } from './useSetOperationPanelUnlessBusy';
import OperationToolbarButton from './OperationToolbarButton';

/** Gates a whole-image derived-structure tool reports on when disabled. */
interface WholeImageToolGates {
    /**
     * Why the IMAGE cannot feed the tool, or undefined when it can. Each
     * tool resolves this itself because each reads the image differently;
     * the gates below describe the DESTINATION and are shared.
     */
    imageReasonId: string | undefined;
    hasStructureSet: boolean;
    /**
     * Why no view shows what the tool must show before it writes, or
     * undefined when one does. Read only once a structure set is loaded:
     * the views are the edit target's.
     */
    viewReasonId?: string;
    isStructureSetApproved: boolean;
    isBulkSelectionActive: boolean;
}

/**
 * The i18n id of the reason a whole-image tool cannot be started, in the
 * order the user would fix them, or undefined when it can. A disabled tool
 * with no reason (no structure set) or with the wrong one ("requires a CT"
 * when nothing is loaded at all) tells the user to look in the wrong place.
 */
function wholeImageToolDisabledReasonId(
    gates: WholeImageToolGates
): string | undefined {
    if (gates.imageReasonId !== undefined) {
        return gates.imageReasonId;
    }
    if (!gates.hasStructureSet) {
        return 'tool.disabled.noStructureSet';
    }
    if (gates.viewReasonId !== undefined) {
        return gates.viewReasonId;
    }
    if (gates.isStructureSetApproved) {
        return 'tool.disabled.approved';
    }
    if (gates.isBulkSelectionActive) {
        return 'tool.disabled.bulkSelection';
    }
    return undefined;
}

/**
 * Why the image cannot feed threshold segmentation. Either unit will do:
 * the panel offers the presets the image's own unit supports
 * (SR-WOR-H-27-2 for HU, SR-WOR-H-27-3 for SUVbw), and a series is one
 * or the other, never both.
 */
function thresholdSegImageReasonId(
    image: Image3D | undefined
): string | undefined {
    if (!image) {
        return 'tool.disabled.noImage';
    }
    if (!image.isHuQuantified && !image.isSuvQuantified) {
        return 'tool.disabled.notQuantified';
    }
    return undefined;
}

/**
 * The domain owns the couch verdict; this only chooses its wording. The
 * pose reason exists because placement is orientation-safe only while the
 * image column direction points at the treatment table, which acquisition
 * guarantees and a reformat quietly revokes (see the premise in
 * `isPatientPositionInconsistentWithGrid`); the oblique reason is the
 * user-facing half of `placeCouchModel`'s throw, so a clinician sees a
 * disabled button with a reason instead of an error after clicking.
 */
const COUCH_BLOCKER_MESSAGE_ID: Record<CouchPlacementBlocker, string> = {
    'no-image': 'tool.disabled.noImage',
    'not-hu-quantified': 'tool.disabled.notCt',
    'grid-not-axis-aligned': 'tool.disabled.oblique',
    'grid-not-transverse': 'tool.disabled.notTransverse',
    'patient-position-mismatch': 'tool.disabled.poseMismatch',
};

export default function OperationsSection() {
    const {
        selectedStructureSet,
        selectedRoiId,
        isStructureSetApproved,
        isBulkSelectionActive,
        structureSetEditDisabled,
        isPrimaryImageIncomplete,
        imageIncompleteReason,
    } = useStructureToolGating();
    const intl = useIntl();
    const { environment } = useActiveEditTarget();
    const image = environment?.image?.parent;
    const acquiredSliceShown = useAcquiredPrimarySliceShown(environment);
    const [panelState, setPanelState] = useRecoilState(operationPanelState);
    const setPanelStateUnlessBusy = useSetOperationPanelUnlessBusy();

    // Boolean and margin always need a writable structure set: even when
    // targeting an existing ROI the new contour data has to land somewhere,
    // and an approved structure set rejects creating or modifying ROIs.
    const isDisabled = structureSetEditDisabled;
    const isRoiDisabled = isDisabled || selectedRoiId === null;
    // The image gate outranks approval in the message: an approved structure
    // set is something the user can unapprove, an image that has not finished
    // arriving is not.
    const approvedReason = isPrimaryImageIncomplete
        ? imageIncompleteReason
        : isStructureSetApproved
          ? intl.formatMessage({ id: 'tool.disabled.approved' })
          : undefined;

    // Threshold segmentation takes the IMAGE as input, not a ROI, so it
    // gates on a writable structure set to receive the result plus the
    // domain quantification — never on ROI selection.
    // The image is the edit target's, and with no structure set there is no
    // edit target: the missing set is then the reason, not a missing image.
    // The steps that show this toolbar always have an image loaded.
    const thresholdSegImageReason = selectedStructureSet
        ? thresholdSegImageReasonId(image)
        : undefined;
    // Conditions under which an OPEN panel can no longer do its job, and
    // must therefore close. Deliberately excludes bulk selection: closing
    // on that tore the panel down mid-configuration on a ctrl-click in the
    // structure list, silently taking the user's region with it.
    const thresholdSegUnusable =
        !selectedStructureSet ||
        isStructureSetApproved ||
        thresholdSegImageReason !== undefined;
    // Conditions under which the tool cannot be STARTED. A bulk selection
    // is a different task — the bulk panel owns the structure list then,
    // and every other tool in the toolbar is unavailable — so offering the
    // one remaining enabled button invites a mode the user did not intend.
    // Gating only the button, never the teardown above, keeps the
    // regression that rule caused from returning.
    // An image that is not the whole volume gates here rather than in the
    // teardown above. Threshold segmentation reads every voxel
    // (`Image3D.pixelData`, which refuses a partial volume), so it cannot
    // run — but "still loading" resolves on its own within seconds, and
    // tearing an open panel down for a state that fixes itself would
    // discard the user's configuration for nothing.
    const isThresholdSegDisabled =
        thresholdSegUnusable ||
        isBulkSelectionActive ||
        isPrimaryImageIncomplete;
    const thresholdSegReason = useMemo<string | undefined>(() => {
        // Ahead of the id-based reasons: this one carries slice counts, so it
        // is a formatted string rather than a message id.
        if (isPrimaryImageIncomplete) {
            return imageIncompleteReason;
        }
        const id = wholeImageToolDisabledReasonId({
            imageReasonId: thresholdSegImageReason,
            hasStructureSet: !!selectedStructureSet,
            isStructureSetApproved,
            isBulkSelectionActive,
        });
        return id === undefined ? undefined : intl.formatMessage({ id });
    }, [
        thresholdSegImageReason,
        selectedStructureSet,
        isStructureSetApproved,
        isPrimaryImageIncomplete,
        imageIncompleteReason,
        isBulkSelectionActive,
        intl,
    ]);

    // The couch reads the image through the domain predicate alone, so
    // its verdict here, in detection and in placement cannot drift apart.
    // As for threshold segmentation, the image is the edit target's, so
    // with no structure set the missing set is the reason.
    const couchBlocker = selectedStructureSet
        ? couchPlacementBlocker(image)
        : undefined;
    const couchImageReason =
        couchBlocker === undefined
            ? undefined
            : COUCH_BLOCKER_MESSAGE_ID[couchBlocker];

    // Same split as threshold segmentation above: what closes an OPEN
    // panel excludes bulk selection, what disables the BUTTON includes it.
    // Completeness sits beside the domain predicate, not inside it: it is
    // a loading state of the session, resolved from the stream's recorded
    // progress (`usePrimaryImageCompletenessGate`), whereas the predicate
    // judges the image's geometry and stays framework-free. Like threshold
    // segmentation, it gates the button and not the teardown: detection
    // takes a median over Z and placement extrudes over every slice, so
    // neither may run on a part-volume, but "still loading" fixes itself.
    // The line and its preview draw only on a view that shows an acquired
    // slice of the primary image, and a couch the user cannot see placed
    // must not be offered, so the button also waits for such a view.
    const couchViewReason = acquiredSliceShown
        ? undefined
        : 'tool.disabled.acquisitionPlaneNotShown';
    const couchUnusable =
        !selectedStructureSet ||
        isStructureSetApproved ||
        couchImageReason !== undefined;
    const isCouchDisabled =
        couchUnusable ||
        couchViewReason !== undefined ||
        isBulkSelectionActive ||
        isPrimaryImageIncomplete;
    const couchReason = useMemo<string | undefined>(() => {
        // Ahead of the id-based reasons, as for threshold segmentation: the
        // counts make it a formatted string, and "no image" cannot compete
        // with it because an absent image reports complete.
        if (isPrimaryImageIncomplete) {
            return imageIncompleteReason;
        }
        const id = wholeImageToolDisabledReasonId({
            imageReasonId: couchImageReason,
            hasStructureSet: !!selectedStructureSet,
            viewReasonId: couchViewReason,
            isStructureSetApproved,
            isBulkSelectionActive,
        });
        return id === undefined ? undefined : intl.formatMessage({ id });
    }, [
        couchImageReason,
        couchViewReason,
        selectedStructureSet,
        isStructureSetApproved,
        isPrimaryImageIncomplete,
        imageIncompleteReason,
        isBulkSelectionActive,
        intl,
    ]);

    const handleBooleanToggle = useCallback(() => {
        setPanelStateUnlessBusy((prev) =>
            prev.type === 'boolean-operation'
                ? { type: 'closed' }
                : { type: 'boolean-operation' }
        );
    }, [setPanelStateUnlessBusy]);

    const handleMarginToggle = useCallback(() => {
        setPanelStateUnlessBusy((prev) =>
            prev.type === 'margin'
                ? { type: 'closed' }
                : { type: 'margin', mode: 'symmetric' }
        );
    }, [setPanelStateUnlessBusy]);

    const handleThresholdSegToggle = useCallback(() => {
        setPanelStateUnlessBusy((prev) =>
            prev.type === 'threshold-segmentation'
                ? { type: 'closed' }
                : { type: 'threshold-segmentation' }
        );
    }, [setPanelStateUnlessBusy]);

    const handleCouchToggle = useCallback(() => {
        setPanelStateUnlessBusy((prev) =>
            prev.type === 'couch' ? { type: 'closed' } : { type: 'couch' }
        );
    }, [setPanelStateUnlessBusy]);

    // Disabling the button does not close an already-open panel.
    useEffect(() => {
        if (
            panelState.type === 'boolean-operation' &&
            (isDisabled || isStructureSetApproved)
        ) {
            setPanelState({ type: 'closed' });
        } else if (
            panelState.type === 'margin' &&
            (isRoiDisabled || isStructureSetApproved)
        ) {
            setPanelState({ type: 'closed' });
        } else if (
            (panelState.type === 'threshold-segmentation' &&
                thresholdSegUnusable) ||
            (panelState.type === 'couch' && couchUnusable)
        ) {
            setPanelState({ type: 'closed' });
        }
    }, [
        panelState.type,
        isDisabled,
        isRoiDisabled,
        isStructureSetApproved,
        thresholdSegUnusable,
        couchUnusable,
        setPanelState,
    ]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.25,
            }}
        >
            {/* Threshold segmentation first: it is the whole-image entry
                point of the section (the other operations start from a
                selected ROI), so it leads the column. */}
            <OperationToolbarButton
                icon={<BodyContourIcon />}
                toolName={intl.formatMessage({
                    id: 'toolbar.thresholdSeg.tooltip',
                })}
                disabledReason={
                    isThresholdSegDisabled ? thresholdSegReason : undefined
                }
                selected={panelState.type === 'threshold-segmentation'}
                disabled={isThresholdSegDisabled}
                onClick={handleThresholdSegToggle}
                dockAnchor="threshold-segmentation"
                data-testid="threshold-seg-button"
            />

            <OperationToolbarButton
                icon={<BoolIcon />}
                toolName={intl.formatMessage({
                    id: 'toolbar.booleanOperation.tooltip',
                })}
                disabledReason={approvedReason}
                selected={panelState.type === 'boolean-operation'}
                disabled={isDisabled || isStructureSetApproved}
                onClick={handleBooleanToggle}
                dockAnchor="boolean-operation"
                data-testid="op-boolean"
            />

            <OperationToolbarButton
                icon={<MarginIcon />}
                toolName={intl.formatMessage({ id: 'toolbar.margin.tooltip' })}
                disabledReason={approvedReason}
                selected={panelState.type === 'margin'}
                disabled={isRoiDisabled || isStructureSetApproved}
                onClick={handleMarginToggle}
                dockAnchor="margin"
                data-testid="op-margin"
            />

            <OperationToolbarButton
                icon={<CouchIcon />}
                toolName={intl.formatMessage({ id: 'toolbar.couch.tooltip' })}
                disabledReason={isCouchDisabled ? couchReason : undefined}
                selected={panelState.type === 'couch'}
                disabled={isCouchDisabled}
                onClick={handleCouchToggle}
                dockAnchor="couch"
                data-testid="couch-button"
            />
        </Box>
    );
}
